import IconButton, { IconButtonProps } from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { alpha } from "@mui/material/styles";
import { forwardRef } from "react";

import { colors } from "@/theme";

const SKY = "#5BC8FF";
const DEEP_INK = "#0B63D6";

/** The header's button size; the avatar beside them matches it. */
export const HEADER_BUTTON_SIZE = { xs: 34, sm: 38 };

interface HeaderIconButtonProps extends Omit<IconButtonProps, "title"> {
  /** What the button does, in the player's language: its tooltip and its name. */
  label: string;
  /** Lit while the thing it opens is open: the chat, the notifications. */
  active?: boolean;
}

/**
 * One of the round icon buttons in the header: theme, chat, notifications.
 *
 * They were three bare icons with three different hover behaviours - one faded,
 * one did nothing, one greyed - and names only a screen reader was told, in
 * English. This is the one shape they share: a tinted disc that lights in the
 * brand blue under the pointer and while what it opens is open, with a tooltip
 * that says what it is.
 */
const HeaderIconButton = forwardRef<HTMLButtonElement, HeaderIconButtonProps>(
  ({ label, active = false, sx, children, ...props }, ref) => (
    <Tooltip title={label} arrow>
      <IconButton
        ref={ref}
        size="small"
        aria-label={label}
        {...props}
        sx={[
          (theme) => ({
            width: HEADER_BUTTON_SIZE,
            height: HEADER_BUTTON_SIZE,
            flexShrink: 0,
            borderRadius: "50%",
            fontSize: 20,
            border: "1px solid",
            borderColor: alpha(colors.prime, active ? 0 : 0.22),
            // Blue ink on a blue tint, deep in the light scheme and sky in the
            // dark one. They were the grey of body text, which on the header
            // read as switched off. The one that is open is filled.
            color: active ? colors.white : DEEP_INK,
            backgroundColor: active ? colors.prime : alpha(colors.prime, 0.1),
            boxShadow: active ? `0 4px 12px ${alpha(colors.prime, 0.4)}` : "none",
            transition:
              "background-color 160ms ease, color 160ms ease, border-color 160ms ease, box-shadow 160ms ease, transform 120ms ease",
            "&:hover": {
              color: active ? colors.white : DEEP_INK,
              backgroundColor: active ? colors.prime : alpha(colors.prime, 0.2),
              borderColor: alpha(colors.prime, active ? 0 : 0.5),
            },
            "&:active": {
              transform: "scale(0.92)",
              backgroundColor: active ? colors.prime : alpha(colors.prime, 0.28),
            },
            ...theme.applyStyles("dark", {
              color: active ? colors.white : SKY,
              backgroundColor: active ? colors.prime : alpha(SKY, 0.1),
              borderColor: alpha(SKY, active ? 0 : 0.24),
              "&:hover": {
                color: active ? colors.white : SKY,
                backgroundColor: active ? colors.prime : alpha(SKY, 0.2),
                borderColor: alpha(SKY, active ? 0 : 0.55),
              },
              "&:active": { backgroundColor: active ? colors.prime : alpha(SKY, 0.28) },
            }),
            "&:focus-visible": { outline: `2px solid ${SKY}`, outlineOffset: 2 },
            "@media (prefers-reduced-motion: reduce)": {
              transition: "none",
              "&:active": { transform: "none" },
            },
          }),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {children}
      </IconButton>
    </Tooltip>
  ),
);
HeaderIconButton.displayName = "HeaderIconButton";

/**
 * The count or dot on a header button, one style for all of them: ringed in
 * the header's colour so it lifts off the disc it sits on.
 */
export const headerBadgeSx = (background: string, ink: string = colors.white) => ({
  flexShrink: 0,
  "& .MuiBadge-badge": {
    color: ink,
    backgroundColor: background,
    border: "2px solid",
    borderColor: "background.default",
    boxSizing: "content-box",
  },
  "& .MuiBadge-standard": {
    minWidth: 14,
    height: 14,
    px: 0.4,
    fontSize: 10,
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
  },
  "& .MuiBadge-dot": { width: 8, height: 8, borderRadius: "50%" },
});

export default HeaderIconButton;
